import React, { createContext, useContext, FC, useState, useEffect } from 'react';
import { Account } from 'eve-account-client';
import { useAuthState, AuthStates, useLogout } from '@vis-auth/react';
import useAccountApi from '../../services/AccountApi';
import { useEnvironment } from '../Environment/EnvironmentContext';
import Environment from '../../constants/Environment';
import { AccountWithEvePlusEligibility } from '../../models/AccountWithEvePlusEligibility';

const AccountContext = createContext<AccountWithEvePlusEligibility | null>(null);

export const useAccount = (): AccountWithEvePlusEligibility => {
  const account = useContext(AccountContext);

  if (!account) {
    throw new Error('useAccount must be used inside AccountProvider');
  }

  return account;
};

export const AccountProvider: FC = ({ children }) => {
  const [account, setAccount] = useState<Account | null>(null);
  const [isEvePlusEligible, setIsEvePlusEligible] = useState<boolean>(false);
  const [error, setError] = useState(false);

  const authState = useAuthState();
  const accountApi = useAccountApi();
  const { ENVIRONMENT_NAME, MOCK_ACCOUNT, MOCK_ACCOUNT_ID } = useEnvironment();
  const logout = useLogout();

  if (authState.state !== AuthStates.authenticated) {
    throw new Error('AccountProvider must be used inside SecureRoute');
  }

  // Todo: get user ID from VIS Auth Client hook once it is available
  let accountId = authState.user?.account_id?.toString();

  if (
    (ENVIRONMENT_NAME === Environment.LOCAL || ENVIRONMENT_NAME === Environment.DEVTEST) &&
    MOCK_ACCOUNT &&
    MOCK_ACCOUNT_ID
  ) {
    accountId = MOCK_ACCOUNT_ID;
  }

  if (accountId === undefined) {
    throw new Error('Account ID has not been defined');
  }

  useEffect(() => {
    const fetchAccount = async (): Promise<void> => {
      try {
        const [accountResponse, evePlusEligibilityResponse] = await Promise.all([
          accountApi.getAccountById({ accountId: accountId as string }),
          accountApi.getEverifyPlusEligibility({
            accountId: accountId as string,
          }),
        ]);
        setIsEvePlusEligible(evePlusEligibilityResponse.isEligible || false);

        setAccount(accountResponse);
      } catch (e: any) {
        if (e.status === 401) {
          logout();
        } else {
          setError(true);
        }
      }
    };

    fetchAccount();
  }, [accountApi, accountId, logout]);

  if (error) {
    throw new Error('Could not fetch account information');
  }

  if (account) {
    return (
      <AccountContext.Provider value={{ ...account, isEvePlusEligible }} data-testid="account-context-provider">
        {children}
      </AccountContext.Provider>
    );
  }

  return <div data-testid="account-context-provider">Loading...</div>;
};
